#include "_prelude_fog.fragment.glsl"
#include "_prelude_lighting.glsl"
#include "_prelude_shadow.fragment.glsl"
#include "_prelude_indicator_cutout.fragment.glsl"
#include "_prelude_feature_cutout.fragment.glsl"

uniform lowp float u_device_pixel_ratio;
uniform highp float u_width_scale;
uniform float u_alpha_discard_threshold;
uniform lowp float u_opacity_multiplier;
uniform bool u_clip_to_tile_borders;

in vec4 v_width2_dilute;
in vec2 v_normal;
in float v_gamma_scale;
in vec2 v_tile_pos;

#ifdef LINE_ROUND_JOIN_CLIP_BORDER_OVERLAP
in highp vec4 v_round_join_conflict_offset;
in highp vec4 v_round_join_conflict_dir;
in mediump vec2 v_round_join_conflict_test;
#endif

#ifdef ELEVATED_ROADS
in highp float v_road_z_offset;
#endif
#ifdef VARIABLE_LINE_WIDTH
in float stub_side;
#endif

uniform bool u_has_dash;

uniform sampler2D u_dash_image;
uniform highp float u_floor_width_scale;

in highp vec2 v_tex;

#ifdef DEBUG_ELEVATION_ID
in vec3 v_elevation_id_col;
#endif

#if defined(RENDER_LINE_GRADIENT) || defined(RENDER_LINE_BORDER_GRADIENT) || defined(RENDER_LINE_TRIM_OFFSET)
in highp vec3 v_uv;
#endif

#ifdef RENDER_LINE_GRADIENT
uniform sampler2D u_gradient_image;
#endif

#ifdef RENDER_LINE_BORDER_GRADIENT
uniform sampler2D u_border_gradient_image;
#endif

#ifdef RENDER_LINE_TRIM_OFFSET
uniform highp vec2 u_trim_offset;
uniform highp vec2 u_trim_fade_range;
uniform highp vec2 u_trim_gradient_mix_range;
uniform lowp vec4 u_trim_color;
#endif

#ifdef INDICATOR_CUTOUT
in highp float v_z_offset;
#endif

#ifdef RENDER_SHADOWS
uniform bool u_emissive_in_shadows;
uniform vec3 u_ground_shadow_factor;

in highp vec4 v_pos_light_view_0;
in highp vec4 v_pos_light_view_1;
in highp float v_depth;
#endif

float luminance(vec3 c) {
    // Digital ITU BT.601 (Y = 0.299 R + 0.587 G + 0.114 B) approximation
    return (c.r + c.r + c.b + c.g + c.g + c.g) * 0.1667;
}

in highp vec4 v_line_color;
in lowp vec4 v_border_color;
in lowp float v_opacity;
in lowp float v_blur;
in lowp float v_floorwidth;
in lowp float v_border_width;
in lowp float v_emissive_strength;
// x = dash pattern length in tile units, y = dash coverage fraction in [0, 1] — computed in the
// vertex shader from the UBO/uniform-sourced dash properties, which the fragment shader has no
// access to. See line.vertex.glsl for details.
in highp vec2 v_dash;

#ifdef LINE_ROUND_JOIN_CLIP_BORDER_OVERLAP
// Large distance sentinel that makes the conflict test find nothing for a zero-flagged conflict segment.
const highp float ROUND_JOIN_NO_CONFLICT = 32768.0;

// Distance from this fragment to a conflicting segment's centre line.
// conflictOffset runs from the segment's first coordinate to this fragment.
// conflictDir runs from its first coordinate to its second, both already in pixel space.
highp float roundJoinConflictDistance(highp vec2 conflictOffset, highp vec2 conflictDir, mediump float test) {
    if (test <= 0.0) {
        return ROUND_JOIN_NO_CONFLICT;
    }
    highp float dirLenSq = max(dot(conflictDir, conflictDir), 1e-6);
    highp float t = clamp(dot(conflictOffset, conflictDir) / dirLenSq, 0.0, 1.0);
    return length(conflictOffset - t * conflictDir);
}

// How far inside (testBounds.x, testBounds.y) this fragment is: 1 well inside, 0 well outside.
// pxStep is passed in rather than taken from fwidth() to ensure that no derivative
// ends up in non-uniform control flow.
float insideRoundJoinTestArea(highp float dist, vec2 testBounds, float pxStep) {
    float inside = 1.0 - smoothstep(testBounds.y - pxStep, testBounds.y + pxStep, dist);
    float pastGap = testBounds.x > 0.0 ? smoothstep(testBounds.x - pxStep, testBounds.x + pxStep, dist) : 1.0;
    return inside * pastGap;
}

// How far inside the conflicting segment's whole 'body' this fragment is: 1 well inside, 0 well outside.
// 'Body' stops a pixel short of its outer edge, where its own antialiasing begins and it no longer covers a pixel completely.
float insideRoundJoinConflictBody(highp float dist, vec3 testBounds, float pxStep) {
    vec2 body = vec2(testBounds.x, testBounds.y - pxStep);
    return insideRoundJoinTestArea(dist, body, pxStep);
}

// How far inside the conflicting segment's 'fill' this fragment is: 1 well inside, 0 well outside.
// 'Fill' is its body inset by its border width on whichever sides it has one.
float insideRoundJoinConflictFill(highp float dist, vec3 testBounds, float pxStep) {
    float gap = testBounds.x;
    float outer = testBounds.y;
    float border = testBounds.z;
    vec2 fill = vec2(gap + (gap > 0.0 ? border : 0.0), max(outer - border, 0.0));
    return insideRoundJoinTestArea(dist, fill, pxStep);
}
#endif

float linearstep(float edge0, float edge1, float x) {
    return  clamp((x - edge0) / (edge1 - edge0), 0.0, 1.0);
}

void main() {
    // Paint properties were read from the UBO (non-premultiplied for the colors) in the
    // vertex shader and passed here as varyings. blur is reassigned below under
    // VARIABLE_LINE_WIDTH, so it (like the rest) needs a mutable local copy — GLSL ES 3.00
    // in varyings aren't l-values in the fragment shader.
    highp vec4 color = vec4(v_line_color.rgb * v_line_color.a, v_line_color.a);
    float floorwidth = v_floorwidth;
    float blur = v_blur;
    float opacity = v_opacity;
    float border_width = v_border_width;
    vec4 border_color = vec4(v_border_color.rgb * v_border_color.a, v_border_color.a);
    float emissive_strength = v_emissive_strength;

    // Calculate the distance of the pixel from the line in pixels.
    float dist = length(v_normal) * v_width2_dilute.x;

    // The distance over which the line edge fades out.
    // Note: the same ANTIALIASING value is used in the line vertex shader.
    float ANTIALIASING = 1.0 / u_device_pixel_ratio / 2.0;
#ifdef RENDER_LINE_BORDER
#ifndef VARIABLE_LINE_WIDTH
    ANTIALIASING *= 8.0;
#endif
#endif

    // Calculate the antialiasing fade factor. This is either when fading in
    // the line in case of an offset line (v_width2_dilute.y) or when fading out
    // (v_width2_dilute.x)
#ifdef VARIABLE_LINE_WIDTH
    blur = mix(blur, 0.0, stub_side);
#endif

    float diluted_opacity = opacity * v_width2_dilute.z;

    // Compute blur alpha factor.
    float blur2 = (u_width_scale * blur + 1.0 / u_device_pixel_ratio) * v_gamma_scale;
    float alpha = clamp(min(dist - (v_width2_dilute.y - blur2), v_width2_dilute.x - dist) / blur2, 0.0, 1.0);

    float pxStep;
    float delta;
    // Coverage of this line's border band: 0 in the border, 1 in the fill.
    // Declared and resolved early to use in the round-join clip tests below
    float alpha2 = 1.0;
#ifdef RENDER_LINE_BORDER
#ifndef VARIABLE_LINE_WIDTH
    // Calculate the rate of change of the distance across the line.
    pxStep = fwidth(dist);
    // Find the distance to the closer edge of the line.
    // Note: outset (v_width2_dilute.x) and inset (v_width2_dilute.y) are computed in the
    // line vertex shader with an extra ANTIALIASING gap. Take this into account when
    // finding the exact inner and outer edge positions.
    float out_edge = v_width2_dilute.x - dist;
    float in_edge = dist - (v_width2_dilute.y - 2.0 * ANTIALIASING);
    delta = v_width2_dilute.y > 0.0 ? min(in_edge, out_edge) : out_edge;
    // Compute distance based anti-aliasing alpha factor to smooth line edges.
    float edge = ANTIALIASING;
    alpha = delta > 0.0 ? smoothstep(edge - pxStep, u_width_scale * blur + edge + pxStep, delta) : 0.0;

    // Compute distance based anti-aliasing alpha factor to smooth line borders.
    float borderPx = border_width * u_width_scale;
    float edge2 = borderPx + ANTIALIASING;
    alpha2 = smoothstep(edge2 - pxStep, edge2 + pxStep, delta);

#ifdef LINE_ROUND_JOIN_CLIP_BORDER_OVERLAP
    // Perform conflict tests if any for this segment's border fragments gated by (alpha > 0.0 && alpha2 < 1.0)
    if (alpha > 0.0 && alpha2 < 1.0 &&
        (v_round_join_conflict_test.x > 0.0 || v_round_join_conflict_test.y > 0.0)) {
        highp float prevDist = roundJoinConflictDistance(v_round_join_conflict_offset.xy,
                                                        v_round_join_conflict_dir.xy,
                                                        v_round_join_conflict_test.x);
        highp float nextDist = roundJoinConflictDistance(v_round_join_conflict_offset.zw,
                                                        v_round_join_conflict_dir.zw,
                                                        v_round_join_conflict_test.y);

        // The conflicting segment's test bounds {x: gap width, y: outer, z: border}.
        // With an assumption that line-width, line-gap-width and line-border-width are constant along a feature.
        vec3 testBounds = vec3(max(v_width2_dilute.y - ANTIALIASING, 0.0),
                                   v_width2_dilute.x - ANTIALIASING,
                                   borderPx);

        float insideConflictFill = max(insideRoundJoinConflictFill(prevDist, testBounds, pxStep),
                                       insideRoundJoinConflictFill(nextDist, testBounds, pxStep));

        // inside the conflict's fill       -- drop our border, that is the fold;
        // inside the it's border band only -- keep ours, the two share that outline;
        // outside it                       -- keep ours.
        alpha2 = max(alpha2, insideConflictFill);

        // Two-pass stencil path makes antialiasing fragments partly filled but claim stencil buffer
        // based on u_alpha_discard_threshold, when 'line-opacity' < 1.0. It leaves border-background
        // hairline in the border-clipped area, where this and conflict segment disagree regarding the color.
        // Raise own alpha to fix body-coverage conflict.
        // Skipped under line-blur and the dilute scale, which hold coverage below on purpose.
        // dilute_scale is a mediump varying: on FP16 hardware an exact 1.0 can interpolate to just under it
        // and silently disable this fix. 0.004 is ~4 FP16 ULP at 1.0; the slack it allows is under 0.4% coverage.
        const float diluteEpsilon = 0.004;
        if (u_alpha_discard_threshold != 0.0 && blur == 0.0 && v_width2_dilute.z >= 1.0 - diluteEpsilon) {
            float insideConflictBody = max(insideRoundJoinConflictBody(prevDist, testBounds, pxStep),
                                           insideRoundJoinConflictBody(nextDist, testBounds, pxStep));
            alpha = max(alpha, delta >= edge ? insideConflictBody : 0.0);
        }
    }
#endif
#endif
#endif

if (u_has_dash) {
    // v_dash.x/y were computed in the vertex shader from paint_properties.dash (raw atlas
    // descriptor decode moved there — see line.vertex.glsl — since only it has UBO access).
    highp float dash_len = v_dash.x;
    float sdfgamma = ANTIALIASING / float(dash_len);
    float scaled_floorwidth = (floorwidth * u_floor_width_scale);

    // Number of dash-pattern periods covered by one framebuffer pixel.
    float periods_per_pixel = fwidth(v_tex.x);

    // Dash coverage is the fraction of one pattern period covered by dashes, in [0, 1]. It is
    // the average alpha of the fully minified pattern.
    float dash_coverage = v_dash.y;
    // Fade to average dash coverage before the pattern becomes undersampled:
    // start at 0.25 periods per pixel and finish at the Nyquist limit (0.5).
    float fade = linearstep(0.25, 0.5, periods_per_pixel);

    // Widen SDF AA as each pixel covers more of the pattern.
    float gamma = max(sdfgamma / scaled_floorwidth, 0.5 * periods_per_pixel);

    if (fade >= 1.0) {
        // Too minified for sampling.
        alpha *= dash_coverage;
    } else if (periods_per_pixel < 0.1) {
        // One tap.
        float sdfdist = texture(u_dash_image, v_tex).r;
        alpha *= linearstep(0.5 - gamma, 0.5 + gamma, sdfdist);
    } else {
        // Two midpoint samples approximate filtering over the pattern range
        // covered by the pixel. Limit the range to one repeating period.
        float span = min(periods_per_pixel, 1.0);
        float tap_offset = span * 0.25;
        float sdf0 = texture(u_dash_image, vec2(v_tex.x - tap_offset, v_tex.y)).r;
        float sdf1 = texture(u_dash_image, vec2(v_tex.x + tap_offset, v_tex.y)).r;
        alpha *= mix(0.5 * (
            linearstep(0.5 - gamma, 0.5 + gamma, sdf0) +
            linearstep(0.5 - gamma, 0.5 + gamma, sdf1)
        ), dash_coverage, fade);
    }
}

    highp vec4 out_color;
#ifdef RENDER_LINE_GRADIENT
    // For gradient lines, v_uv.xy are the coord specify where the texture will be sampled.
    out_color = texture(u_gradient_image, v_uv.xy);
#else
    out_color = color;
#endif

    float trim_alpha = 1.0;
#ifdef RENDER_LINE_TRIM_OFFSET
    highp float trim_start = u_trim_offset[0];
    highp float trim_end = u_trim_offset[1];
    highp float line_progress = v_uv[2];
    // Mark the pixel to be transparent when:
    // 1. trim_offset range is valid
    // 2. line_progress is within trim_offset range

    // Nested conditionals fixes the issue
    // https://github.com/mapbox/mapbox-gl-js/issues/12013
    if (trim_end > trim_start) {
        highp float start_transition = max(0.0, min(1.0, (line_progress - trim_start) / max(u_trim_fade_range[0], 1.0e-9)));
        highp float end_transition = max(0.0, min(1.0, (trim_end - line_progress) / max(u_trim_fade_range[1], 1.0e-9)));
        highp float transition_factor = min(start_transition, end_transition);
        highp float gradient_trim_color_mix_factor = 0.0;
#ifdef RENDER_LINE_GRADIENT
        gradient_trim_color_mix_factor = smoothstep(u_trim_gradient_mix_range.x, u_trim_gradient_mix_range.y, line_progress);
#endif
        highp vec4 trim_color = mix(u_trim_color, out_color, gradient_trim_color_mix_factor);
        // Note: if u_trim_gradient_mix_range.x < 1.0 the non-trimmed part will use the non-gradient color
        // This sets the usage of line-gradient as a trim-color
        out_color = mix(u_trim_gradient_mix_range.x < 1.0 ? color : out_color, trim_color, transition_factor);
        trim_alpha = 1.0 - transition_factor;
    }
#endif

    if (u_alpha_discard_threshold != 0.0) {
        if (alpha < u_alpha_discard_threshold) {
            discard;
        }
    }

#ifdef RENDER_LINE_BORDER
#ifndef VARIABLE_LINE_WIDTH
    if (alpha2 < 1.) {
#ifdef RENDER_LINE_BORDER_GRADIENT
        // line-border-gradient takes precedence over border_color and the auto-derived border color.
        vec4 border_gradient_color = texture(u_border_gradient_image, v_uv.xy);
        out_color = mix(border_gradient_color * trim_alpha, out_color, alpha2);
#else
        if (border_color.a == 0.0) {
#ifndef RENDER_LINE_GRADIENT
            float Y = (out_color.a > 0.01) ? luminance(out_color.rgb / out_color.a) : 1.; // out_color is premultiplied
            float adjustment = (Y > 0.) ? 0.5 / Y : 0.45;
            if (out_color.a > 0.25 && Y < 0.25) {
                vec3 borderColor = (Y > 0.) ? out_color.rgb : vec3(1, 1, 1) * out_color.a;
                out_color.rgb = out_color.rgb + borderColor * (adjustment * (1.0 - alpha2));
            } else {
                out_color.rgb *= (0.6 + 0.4 * alpha2);
            }
#else
            out_color.rgb *= (0.6 + 0.4 * alpha2);
#endif
        } else {
            out_color = mix(border_color * trim_alpha, out_color, alpha2);
        }
#endif
        out_color *= v_width2_dilute.w;
    }
#endif
#endif

    vec2 cutout_factors = vec2(0.0);
#ifdef FEATURE_CUTOUT
    cutout_factors = get_cutout_factors(gl_FragCoord);
#endif

#ifdef LIGHTING_3D_MODE
    out_color = apply_lighting_with_emission_ground(out_color, emissive_strength);
#ifdef RENDER_SHADOWS
    float light = shadowed_light_factor(v_pos_light_view_0, v_pos_light_view_1, v_depth);
    light = mix(light, 1.0, cutout_factors.y);
    light = u_emissive_in_shadows ? mix(light, 1.0, emissive_strength) : light;
#ifdef ELEVATED_ROADS
    out_color.rgb *= mix(v_road_z_offset != 0.0 ? u_ground_shadow_factor : vec3(1.0), vec3(1.0), light);
#else
    out_color.rgb *= mix(u_ground_shadow_factor, vec3(1.0), light);
#endif // ELEVATED_ROADS
#endif // RENDER_SHADOWS
#endif // LIGHTING_3D_MODE

#ifdef FOG
    out_color = fog_dither(fog_apply_premultiplied(out_color, v_fog_pos));
#endif

    out_color *= (alpha * diluted_opacity * u_opacity_multiplier);

#ifdef INDICATOR_CUTOUT
    out_color = applyCutout(out_color, v_z_offset);
#endif
#ifdef FEATURE_CUTOUT
    float z = 0.0;
#ifdef ELEVATED_ROADS
    z = v_road_z_offset;
#endif
    out_color = apply_feature_cutout(out_color, gl_FragCoord, cutout_factors.x, z);
#endif

#ifdef LINE_BLEND_ADDITIVE
    // In additive blend mode the FBO uses ColorMode.additiveAlphaWeighted ([SRC_ALPHA, ONE, ...]).
    // out_color is fully premultiplied: rgb = C*fa*cov, a = fa*cov
    // (fa = feature colour alpha, cov = AA coverage * opacity).
    //
    // coverage is then SRC_ALPHA weight, and C*fa (feature-premultiplied colour) is the value.
    //
    // Output rgb = color.rgb (= C*fa, before coverage was applied) and alpha = cov
    {
        float cov = alpha * diluted_opacity;
        // color.rgb is the feature-alpha-premultiplied colour before coverage scaling.
        // Recover it by dividing out the coverage factor, so the blend mode can apply it correctly.
        glFragColor = vec4(cov > 0.0 ? out_color.rgb / cov : vec3(0.0), cov);
    }
#else
#ifdef LINE_BLEND_MULTIPLY
    // In multiply blend mode the FBO accumulates per-line multiply factors using
    // ColorMode.multiply ([DST_COLOR, ZERO]).  Each fragment must therefore output
    // the factor by which the background should be scaled for this line at this
    // coverage level:
    //   factor = 1 - a*(1 - C)  (lerp between 1=no-op and C=full multiply)
    // out_color is premultiplied: out_color.rgb = C*a, out_color.a = a, so:
    //   factor.rgb = out_color.rgb + (1.0 - out_color.a)
    // Output alpha=1 so the DST_COLOR blend sees a solid factor with no alpha scaling.
    glFragColor = vec4(out_color.rgb + (1.0 - out_color.a), 1.0);
#else
    glFragColor = out_color;
#endif
#endif

    storeEmissiveColor(glFragColor, emissive_strength);

#ifdef OVERDRAW_INSPECTOR
    glFragColor = vec4(1.0);
#endif

#ifdef DEBUG_ELEVATION_ID
    glFragColor = vec4(v_elevation_id_col, 1.0);
#endif

    if (u_clip_to_tile_borders) {
        if (v_tile_pos.x > 1.0 || v_tile_pos.y > 1.0 || v_tile_pos.x < 0.0 || v_tile_pos.y < 0.0) {
            discard;
        }
    }

    HANDLE_WIREFRAME_DEBUG;
}
